/**
 * Subscription Types
 * Shared enums and helper types for subscription and billing
 *
 * Note: Response types are inferred via Hono RPC
 * Route constants are now defined in the backend
 */

import { formatStorageLimit } from '../utils';

// ============================================================================
// Subscription Types (Enums & Computed Types)
// ============================================================================

/**
 * Subscription Status (from Stripe)
 */
export type SubscriptionStatus =
  | 'active'
  | 'canceled'
  | 'incomplete'
  | 'past_due'
  | 'trialing';

/**
 * Billing Interval
 */
export type BillingInterval = 'monthly' | 'yearly';

// ============================================================================
// Plan Display Types
// ============================================================================

/**
 * Plan data structure for PlanCard components
 * Subset of PlanApiResponse with fields needed for display
 */
export interface PlanCardData {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  monthly_price_cents: number | null;
  yearly_price_cents: number | null;
  is_contact_sales: boolean;
  is_recommended: boolean;
  storage_limit_mb: number | null;
  max_tables: number | null;
  max_team_members: number | null;
  spreadsheet_pulls_limit: number | null;
}

/**
 * Feature item for plan feature lists
 */
export interface PlanFeature {
  text: string;
}

/**
 * Build feature list from plan data for display in PlanCard components
 *
 * @param plan - Plan data to extract features from
 * @returns Array of feature items with display text
 *
 * @example
 * ```ts
 * const features = buildFeatureList(plan);
 * // [{ text: "5 GB storage" }, { text: "Up to 10 tables" }, ...]
 * ```
 */
export function buildFeatureList(plan: PlanCardData): PlanFeature[] {
  const features: PlanFeature[] = [];

  features.push({ text: `${formatStorageLimit(plan.storage_limit_mb)} storage` });

  if (plan.max_tables === null) {
    features.push({ text: 'Unlimited tables' });
  } else {
    features.push({ text: `Up to ${plan.max_tables} tables` });
  }

  if (plan.max_team_members === null) {
    features.push({ text: 'Unlimited team members' });
  } else {
    features.push({ text: plan.max_team_members === 1 ? 'Only 1 team member' : `Up to ${plan.max_team_members} team members` });
  }

  return features;
}

/**
 * Computed Subscription Status Details
 * Helper type for frontend to determine workspace access state
 */
export interface SubscriptionStatusDetails {
  isActive: boolean;
  isTrial: boolean;
  isExpired: boolean;
  daysRemaining: number | null;
  requiresPayment: boolean;
  status: SubscriptionStatus;
}

/**
 * Workspace Feature Limits
 * Computed from plan limits and workspace overrides
 */
export interface WorkspaceLimits {
  storage_limit_mb: number | null;
  max_tables: number | null;
  max_team_members: number | null;
  spreadsheet_pulls_limit: number | null;
}

// ============================================================================
// Request Types
// ============================================================================

/**
 * Request for POST /api/private/workspaces/:id/subscription/checkout
 */
export interface UpgradeSubscriptionRequest {
  plan_id: string;
  billing_interval: BillingInterval;
}
